import React from "react";
import { useRouter } from "next/router";
import { Button } from "@/components/Button/Button";
import { Section } from "@/components/Section/Section";
import { LabelsType } from "@/types/OptionType";
import { Post } from "@/wp/types/client";
import { TeaserSliderElementType } from "@/components/TeaserCard/TeaserCard";
import TeaserSliderModule, {
  TeaserSliderModuleType,
} from "@/modules/TeaserSlider/TeaserSlider";
import LinkListModule from "@/modules/LinkListModule/LinkListModule";
import { LinkListModuleType } from "@/modules/LinkListModule/LinkListModule";
import FormBuilderModule, { FormBuilderType } from "@/modules/Form/FormModule";
import { FormData } from "@/getData/getIndexedFormsData";
import { ModuleOptionsType } from "@/types/ModuleOptionsType";
import { FileType } from "@/types/baseTypes";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

export interface PostFootModuleProps {
  pageData: Post;
  labels: LabelsType;
  relatedMetals?: TeaserSliderElementType[];
  relatedJobs?: number[];
  moduleOptions: ModuleOptionsType;
  csvFile: FileType;
  isNobleElements: boolean;
  blogFormData?: FormData;
  relatedBlogPostIds?: number[];
}

export const PostFootModule = ({
  pageData,
  labels,
  relatedMetals = [],
  relatedJobs = [],
  moduleOptions,
  csvFile,
  isNobleElements,
  blogFormData,
  relatedBlogPostIds = [],
}: PostFootModuleProps) => {
  const router = useRouter();
  const { getLabel } = useGlobalContext();
  const teaserSliderOptions = moduleOptions?.teaser_slider;

  const mockSettings = {
    time_control: "off" as const,
    start_date: "",
    end_date: "",
    weekdays: [],
  };

  const createLinkListLayout = (id: string, postsPerPage: number) => ({
    id,
    module_width: "narrow-frame" as const,
    section_margin_class: "is-gapless-margin-top-bottom" as const,
    section_padding_class: "default" as const,
    background_color_class: "has-hellgrau-bg" as const,
    headline_text_align_class: "has-text-left" as const,
    text_align_class: "has-text-left" as const,
    flex_direction_class: "is-flex-direction-row" as const,
    icon_align_class: "is-vcentered" as const,
    row_direction_class: "is-row-normal-desktop" as const,
    toggle_cardbox: true,
    trenner_anzeigen: false,
    grid_columns: "6" as const,
    posts_per_page: postsPerPage,
  });

  const createLinkListConfig = (
    headline: string,
    type: "jobs" | "insights",
    jobs: number[] | null = null,
    insights: number[] | null = null,
  ) => ({
    headline,
    headline_html_tag: "h2" as const,
    overline: "",
    subline: "",
    type,
    location: false as const,
    jobs,
    insights,
    press: null,
    events: null,
    team: null,
    elements: false as const,
    link: null,
  });

  const renderMetalsFooter = () => {
    if (!relatedMetals.length || !teaserSliderOptions) {
      return null;
    }

    const headlineText = getLabel("more_materials", "Weitere Rohstoffe");

    // Build a mock module data for TeaserSliderModule to match its expected props structure
    const mockData: TeaserSliderModuleType = {
      teaser_slider: {
        headline: headlineText,
        headline_html_tag: "h2",
        overline: "",
        subline: "",
        type: "multiselect",
        posts: [],
        elements: false,
      },
      layout: {
        id: "post-foot-metals-slider",
        module_width: "frameless",
        section_margin_class: "is-gapless-margin-top-bottom",
        section_padding_class: "default",
        background_color_class: "has-hellgrau-bg",
        headline_text_align_class: "has-text-centered",
      },
      settings: mockSettings,
    };

    return (
      <TeaserSliderModule
        data={mockData}
        teaserSliderOptions={teaserSliderOptions}
        teaserSliderData={relatedMetals}
        metalCsvFile={csvFile}
      />
    );
  };

  const renderJobsFooter = () => {
    // Filter out the current job from related jobs
    const filteredJobs = relatedJobs.filter((jobId) => jobId !== pageData.id);

    if (!filteredJobs.length || !moduleOptions?.link_list) {
      // Fallback: primary back button with backend labels
      return (
        <Section
          className="section is-gapless-top mt-1"
          columnsClassName="is-centered"
        >
          <div className="column is-10-tablet is-8-widescreen is-centered">
            <Button
              type={"primary"}
              href={""}
              target={"_self"}
              title={getLabel("back", "Zurück")}
              aria-label={getLabel("back_aria_label", "")}
              onClick={() => router.back()}
              isBack
            >
              <span>{getLabel("back", "Zurück")}</span>
            </Button>
          </div>
        </Section>
      );
    }

    const headlineText = getLabel(
      "module_footer_jobs_headline",
      "Weitere Jobangebote",
    );

    // Build a mock module data for LinkListModule to match its expected props structure
    const mockData: LinkListModuleType = {
      linklist: createLinkListConfig(headlineText, "jobs", filteredJobs),
      layout: createLinkListLayout("post-foot-jobs-linklist", 10),
      settings: mockSettings,
    };

    return (
      <LinkListModule
        data={mockData}
        linkListOptions={moduleOptions.link_list}
        isNobleElements={isNobleElements}
      />
    );
  };

  const renderBackButton = () => {
    return (
      <Section
        className="section is-gapless-top mt-1"
        columnsClassName={"is-centered"}
      >
        <div className="column is-10-tablet is-8-widescreen is-centered">
          <Button
            type={"secondary"}
            href={""}
            target={"_self"}
            title={labels?.find((label) => label.key === "back")?.value || ""}
            onClick={() => router.back()}
            isBack
          >
            <span>
              {labels?.find((label) => label.key === "back")?.value || ""}
            </span>
          </Button>
        </div>
      </Section>
    );
  };

  const renderBlogFooter = () => {
    if (!moduleOptions?.link_list || !moduleOptions?.formbuilder) {
      return renderBackButton();
    }

    const mockFormData = {
      acf_fc_layout: "formbuilder",
      formbuilder: {
        headline: "",
        headline_html_tag: "h2",
        content: "",
        overline: "",
        subline: "",
        link: null,
        form_id: blogFormData?.formId || 43100,
      },
      layout: {
        id: "post-foot-blog-form",
        section_margin_class: "is-gapless-margin-top-bottom",
        section_padding_class: "default",
        background_color_class: "has-weiss-bg",
      },
      settings: mockSettings,
    } as unknown as FormBuilderType;

    const mockListData: LinkListModuleType = {
      linklist: createLinkListConfig(
        getLabel("module_footer_blog_headline", "Weitere Beiträge"),
        "insights",
        null,
        relatedBlogPostIds.length ? relatedBlogPostIds : null,
      ),
      layout: createLinkListLayout("post-foot-blog-linklist", 5),
      settings: mockSettings,
    };

    return (
      <>
        {blogFormData && (
          <FormBuilderModule
            data={mockFormData}
            formData={blogFormData}
            friendlyCaptchaApplicationkey={""}
            formBulderOptions={moduleOptions.formbuilder!}
          />
        )}
        {relatedBlogPostIds.length > 0 && (
          <LinkListModule
            data={mockListData}
            linkListOptions={moduleOptions.link_list!}
            isNobleElements={isNobleElements}
          />
        )}
      </>
    );
  };

  return (
    <>
      {pageData.type === "metals" && renderMetalsFooter()}
      {pageData.type === "jobs" && renderJobsFooter()}
      {pageData.type === "blog" && renderBlogFooter()}
      {["catalog"].includes(pageData.type) && renderBackButton()}
    </>
  );
};
